import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  listTaskShares,
  revokeTaskShare,
  upsertTaskShare,
} from '../api/tasks'
import type { AccessRole, ShareRole, TaskShare } from '../types'

export function TaskShares({
  taskId,
  myRole,
}: {
  taskId: string
  myRole?: AccessRole
}) {
  const canShare = myRole === 'owner' || myRole === 'unscoped'
  const [shares, setShares] = useState<TaskShare[]>([])
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<ShareRole>('monitor')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setShares(await listTaskShares(taskId))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load shares')
    }
  }, [taskId])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function onAdd(e: FormEvent) {
    e.preventDefault()
    const trimmed = email.trim().toLowerCase()
    if (!trimmed) return
    setBusy(true)
    setError(null)
    try {
      await upsertTaskShare(taskId, trimmed, role)
      setEmail('')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Share failed')
    } finally {
      setBusy(false)
    }
  }

  async function onRevoke(grantee: string) {
    setBusy(true)
    setError(null)
    try {
      await revokeTaskShare(taskId, grantee)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Revoke failed')
    } finally {
      setBusy(false)
    }
  }

  const shareHint =
    shares.length === 0
      ? 'Not shared'
      : shares.length === 1
        ? '1 person'
        : `${shares.length} people`

  return (
    <details className="detail-accordion detail-edit" aria-label="Task sharing">
      <summary className="detail-accordion-summary">
        <span className="detail-accordion-title">Sharing</span>
        <span className="detail-accordion-meta">{shareHint}</span>
      </summary>
      <div className="detail-accordion-body">
        {error ? (
          <p className="retry-banner is-error" role="alert">
            {error}
          </p>
        ) : null}
        {shares.length === 0 ? (
          <p className="empty tight">Not shared with anyone yet.</p>
        ) : (
          <ul className="todo-list compact">
            {shares.map((s) => (
              <li key={s.grantee_email} className="todo status-active">
                <span className="todo-disclose spacer" aria-hidden="true" />
                <div className="todo-body">
                  <span className="todo-title">{s.grantee_email}</span>
                  <span className="todo-meta">
                    <span className="pill muted">{s.role}</span>
                  </span>
                </div>
                {canShare ? (
                  <button
                    type="button"
                    className="ghost"
                    disabled={busy}
                    onClick={() => void onRevoke(s.grantee_email)}
                  >
                    Remove
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canShare ? (
          <form className="k-inline-form" onSubmit={(e) => void onAdd(e)}>
            <label className="ctl grow">
              <span>Share with (email)</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                placeholder="teammate@example.com"
                required
              />
            </label>
            <label className="ctl">
              <span>Role</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as ShareRole)}
                disabled={busy}
              >
                <option value="monitor">monitor (no email bodies)</option>
                <option value="collaborator">collaborator (full + edit due/status)</option>
              </select>
            </label>
            <button type="submit" className="login-primary" disabled={busy || !email.trim()}>
              Share
            </button>
          </form>
        ) : null}
      </div>
    </details>
  )
}
